// frontend/src/components/grid/NoRowsOverlay.jsx
// Shared AG Grid no-rows overlay — context-aware empty state.
//
// Two distinct empty states are auto-detected from the grid's filter state:
//   - "filters returned nothing" → always uses generic copy ("No matches")
//   - "no data at all"           → the page's own copy, given by <GridEmptyState>
//
// A page sets its copy by wrapping its <AgGridReact> in <GridEmptyState>
// (all props optional, title/message/action only apply to the "no data" case):
//   title          — bold heading (e.g. "Inventory hasn't synchronized yet")
//   message        — body text
//   action         — { label, onClick } for an optional CTA button
//   filtersActive  — explicit override for filter detection. Pass `true` whenever
//                    the grid has external filters active (search box, custom
//                    dropdowns) that AG Grid itself can't see — typically true
//                    when any of the ItemFilterBar values are non-default.
//                    Falls back to api.isAnyFilterPresent() when not passed.
//
// The copy reaches the overlay by React context. The overlay renders as a
// portal inside <AgGridReact>, so it re-renders with the provider on every
// change, including the tasks between AG Grid showing the overlay and
// ag-grid-react mounting it, when AG Grid applies no change to its own overlay
// params. A page whose copy comes from the response that emptied the grid
// changes it exactly then (NoRowsOverlay.grid.test.jsx).
//
// The filtered-empty case intentionally ignores `title`/`message`/`action` so a
// merchant who filters down to nothing always sees consistent "no matches" guidance.
//
// Empty states render with text only — no icons.

import { createContext, useContext } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './GridOverlays.css';

const EmptyStateContext = createContext({});

export function GridEmptyState({ title, message, action, filtersActive, children }) {
  return (
    <EmptyStateContext.Provider value={{ title, message, action, filtersActive }}>
      {children}
    </EmptyStateContext.Provider>
  );
}

export default function NoRowsOverlay({ api }) {
  const { t } = useTranslation();
  const { title, message, action, filtersActive: filtersActiveProp } = useContext(EmptyStateContext);

  // Explicit prop wins; otherwise rely on AG Grid's own filter knowledge.
  // Most grids in this app filter via ItemFilterBar which bypasses AG Grid, so
  // grids should pass filtersActive explicitly for correct empty-state copy.
  const filtersActive = filtersActiveProp !== undefined
    ? Boolean(filtersActiveProp)
    : api.isAnyFilterPresent();

  if (filtersActive) {
    return (
      <div className="grid-overlay grid-no-rows-overlay" role="status">
        <p className="grid-no-rows-title">{t('grid.empty.filteredTitle')}</p>
        <p className="grid-no-rows-message">{t('grid.empty.filteredMessage')}</p>
      </div>
    );
  }

  return (
    <div className="grid-overlay grid-no-rows-overlay" role="status">
      <p className="grid-no-rows-title">{title || t('grid.empty.defaultTitle')}</p>
      {message && <p className="grid-no-rows-message">{message}</p>}
      {action?.label && (
        <div className="grid-no-rows-action">
          <Button variant="contained" size="small" onClick={action.onClick}>
            {action.label}
          </Button>
        </div>
      )}
    </div>
  );
}
